import { MaterialFileFormFields } from "@/components/commonFormItems/MaterialFileFormFields";
import { SupplierInfoFormItems } from "@/components/commonFormItems/SupplierInfoFormItems";
import ModalSectionHeader from "@/components/ui/ModalSectionHeader";
import { factoryOptions, FREEZE_APPLY_TYPE } from "@/constants";
import { useModal } from "@/hooks/modals/useModal";
import { Col, Form, Input, Row, Select } from "antd";
import type { FormInstance } from "antd";
import { useBlockRecordDetailQuery } from "../hooks/useBlockRecordDetailQuery";
import { useMaterialOptions } from "../hooks/useMaterialOptions";
import type { BlockApplyFormFields } from "../type";
import { useFormEditable } from "../hooks/useFormEditable";

type Props = {
  form: FormInstance<BlockApplyFormFields>;
};

export function BlockApplyForm({ form }: Props) {
  const { props } = useModal("MATERIAL_BLOCK");
  const { supplier, recordId, flowStatus } = props;
  const detail = useBlockRecordDetailQuery(recordId);

  const blockedFactoryValues = Form.useWatch("blockedFactoryValues", form);
  const factoryValues = blockedFactoryValues?.join();

  const { isBlockReasonEditable, isUnblockReasonEditable } = useFormEditable();

  const materialOptions = useMaterialOptions(
    detail ? detail.supplierCode : supplier?.supplierCode!,
    factoryValues,
  );

  /**
   * 是否显示解冻原因
   */
  const showUnblockReason =
    (flowStatus === "已通过" &&
      detail?.applyType === FREEZE_APPLY_TYPE.PENDING_SUBMISSION) ||
    detail?.applyType === FREEZE_APPLY_TYPE.UNDER_APPROVAL ||
    detail?.applyType === FREEZE_APPLY_TYPE.TERMINATED;

  // 冻结类型选项
  const blockTypeOptions = [
    { label: "供应商冻结", value: 0 },
    { label: "物料冻结", value: 1 },
  ];

  const blockType = Form.useWatch("blockType", form);

  // 是否显示物料冻结相关字段
  const showMaterialBlockFields = blockType === 1;

  return (
    <Form
      form={form}
      layout="vertical"
      disabled={!isBlockReasonEditable}
      style={{ paddingRight: 26 }}
    >
      <div>
        <ModalSectionHeader title="供应商信息" />
        <SupplierInfoFormItems<BlockApplyFormFields> />

        <Row gutter={49}>
          <Col span={8}>
            <Form.Item<BlockApplyFormFields>
              label="冻结类型"
              name="blockType"
              rules={[{ required: true, message: "请选择冻结类型" }]}
            >
              <Select
                style={{ width: "100%" }}
                placeholder="请选择冻结类型"
                options={blockTypeOptions}
                onChange={(value) => {
                  if (value === 0) {
                    // 供应商冻结时，清空物料相关字段
                    form.setFieldsValue({
                      blockedFactoryValues: [],
                      blockedFactoryNames: "",
                      blockedMaterialValues: [],
                      blockedMaterialNames: "",
                    });
                  }
                }}
              />
            </Form.Item>
          </Col>

          {showMaterialBlockFields && (
            <>
              <Col span={8}>
                <Form.Item<BlockApplyFormFields>
                  label="禁止供货工厂"
                  name="blockedFactoryValues"
                  rules={[{ required: true, message: "请选择禁止供货工厂" }]}
                >
                  <Select
                    mode="multiple"
                    style={{ width: "100%" }}
                    placeholder="请选择"
                    options={factoryOptions}
                    onChange={(_, options) => {
                      form.setFieldValue(
                        "blockedFactoryNames",
                        Array.isArray(options) &&
                          options.map((option) => option.label).join(),
                      );
                    }}
                  />
                </Form.Item>

                <Form.Item<BlockApplyFormFields>
                  name="blockedFactoryNames"
                  hidden
                >
                  <Input />
                </Form.Item>
              </Col>

              <Col span={8}>
                <Form.Item<BlockApplyFormFields>
                  label="禁止供货物料"
                  name="blockedMaterialValues"
                  rules={[{ required: true, message: "请选择禁止供货物料" }]}
                >
                  <Select
                    mode="multiple"
                    style={{ width: "100%" }}
                    placeholder="请选择"
                    options={materialOptions}
                    onChange={(_, options) => {
                      form.setFieldValue(
                        "blockedMaterialNames",
                        Array.isArray(options) &&
                          options.map((option) => option.label).join(),
                      );
                    }}
                  />
                </Form.Item>

                <Form.Item<BlockApplyFormFields>
                  name="blockedMaterialNames"
                  hidden
                >
                  <Input />
                </Form.Item>
              </Col>
            </>
          )}
        </Row>

        <Row>
          <Col span={24}>
            <Form.Item<BlockApplyFormFields>
              label="冻结原因"
              name="blockReason"
              rules={[{ required: true, message: "请输入冻结原因" }]}
            >
              <Input.TextArea placeholder="请输入" maxLength={300} />
            </Form.Item>
          </Col>
        </Row>

        <MaterialFileFormFields<BlockApplyFormFields>
          required={false}
          label="相关证明文件"
          fieldName="files"
          bindFlow={true}
          hiddenRemove={!isBlockReasonEditable}
        />
      </div>

      {showUnblockReason && (
        <div>
          <ModalSectionHeader title="解冻原因" />
          <Row>
            <Col span={24}>
              <Form.Item<BlockApplyFormFields>
                label=""
                name="unblockReason"
                rules={[{ required: true, message: "请输入解冻原因" }]}
              >
                <Input.TextArea
                  placeholder="请输入"
                  maxLength={300}
                  disabled={!isUnblockReasonEditable}
                />
              </Form.Item>
            </Col>
          </Row>
        </div>
      )}
    </Form>
  );
}
